/* eslint-disable radix */
import { Box, Collapse, IconButton, TableCell } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { Column } from 'types/common';
import { formatMoney } from 'utils/helpers';
import { AccountingType } from 'v2-types/order';
import { InvolvedPartyTable } from 'views/orders/charges/components/GeneralChargesTable/components/ChargesTableRow/components/PartiesInvolvedRow/components/InvolvedPartyTable';

import InfoComponent from '../info-component';
import { ColumnsType } from '../prototype-new-table-charges/services';
import { CellIndicator, CellText } from '../table-cells';

export type columnsDataSet = {
  indexRow: number;
  id: string,
  description: string;
  chargeData: AccountingType,
  number: number;
}

type RendererParams = {
  row: columnsDataSet;
  column: Column;
  index: number;
  indexRow?: number;
  shouldCollapse?: boolean
};

const isPersistedEntry = (row: columnsDataSet) => (row.chargeData?._id?.length ?? 0) > 10;

type ExpandColumn = {
  header?: {
    title: string;
    width?: string;
    minWidth?: string;
  };
  renderer: (params: RendererParams) => React.ReactElement;
};

const buildExpandColumn = (
  itemsCollapsed: { [key: string]: boolean } | undefined,
  onToggleCollapse: ((item: columnsDataSet) => void) | undefined
): ExpandColumn[] => (onToggleCollapse ? [{
  header: {
    title: '',
    width: '40px',
    minWidth: '40px'
  },
  renderer: ({ row }: RendererParams) => (
    isPersistedEntry(row) ? (
      <IconButton
        size="small"
        onClick={(event) => {
          event.stopPropagation();
          onToggleCollapse(row);
        }}
      >
        <FontAwesomeIcon
          icon={itemsCollapsed?.[row.id] ? 'chevron-up' : 'chevron-down'}
          size="sm"
        />
      </IconButton>
    ) : <></>
  )
}] : []);

export const generatekDues = (
  t,
  theme,
  title,
  handleEditEntry,
  handleDelete,
  handleChangePayees?: (item: columnsDataSet) => void,
  handleChangePayers?: (item: columnsDataSet) => void,
  itemsCollapsed?: { [key: string]: boolean },
  onToggleCollapse?: (item: columnsDataSet) => void
): ColumnsType => ({
  paid_seller: {
    header: {
      title,
      textColor: theme.palette.primary.light
    },
    columns: [
      ...buildExpandColumn(itemsCollapsed, onToggleCollapse),
      {
        header: {
          title: '',
          width: '50px',
          minWidth: '50px'
        },
        renderer: ({ row }: RendererParams) => (
          <CellIndicator
            isActive={false}
            position={row.number}
          />
        )
      },
      {
        header: {
          title: t('charges:statement-text'),
          textColor: theme.palette.primary.light
        },
        renderer: ({ row }: RendererParams) => (
          <CellText plain position="left" data={row.description} />
        )
      },
      {
        header: {
          title: t('lender:amount'),
          textColor: theme.palette.primary.light
        },
        renderer: ({ row }: RendererParams) => (
          <CellText plain position="right" data={formatMoney(row?.chargeData?.amount || 0)} />
        )
      },
      {
        header: {
          title: '',
          width: '50px',
          minWidth: '50px'
        },
        renderer: ({ row }: RendererParams) => (
          isPersistedEntry(row) ? (
            <InfoComponent
              onClickEdit={() => {
                handleEditEntry(row);
              }}
              onChangePayees={isPersistedEntry(row) && handleChangePayees ? () => {
                handleChangePayees(row);
              } : undefined}
              onChangePayers={isPersistedEntry(row) && handleChangePayers ? () => {
                handleChangePayers(row);
              } : undefined}
              onClickDelete={(row.chargeData.kind.includes('manual') && row.chargeData._id.length > 10) ? () => {
                handleDelete(row);
              } : undefined}
            />
          ) : (
            <IconButton
              size="small"
              aria-label={t('charges:add-entry')}
              onClick={() => handleEditEntry(row)}
            >
              <FontAwesomeIcon icon="plus" size="sm" />
            </IconButton>
          )
        )
      }
    ]
  }
});

export const generateKDueAdjustments = (
  t,
  theme,
  title,
  handleEditEntry,
  handleDelete,
  isAddendum?: boolean,
  handleChangePayees?: (item: columnsDataSet) => void,
  handleChangePayers?: (item: columnsDataSet) => void,
  itemsCollapsed?: { [key: string]: boolean },
  onToggleCollapse?: (item: columnsDataSet) => void
): ColumnsType => ({
  paid_seller: {
    header: {
      title,
      padding: theme.spacing(2.3125)
    },
    columns: [
      ...buildExpandColumn(itemsCollapsed, onToggleCollapse),
      {
        header: {
          title: '',
          disabled: true,
          width: '50px',
          minWidth: '50px'
        },
        renderer: ({ row }: RendererParams) => (
          <CellIndicator
            isActive={false}
            position={row?.number}
            StringValue={isAddendum ? 'AD' : undefined}
          />
        )
      },
      {
        header: {
          title: '',
          disabled: true,
        },
        renderer: ({ row }: RendererParams) => (
          <CellText plain position="left" data={row.description} />
        )
      },
      {
        header: {
          title: '',
          disabled: true,
          textColor: theme.palette.primary.light
        },
        renderer: ({ row }: RendererParams) => (
          <CellText plain position="right" data={formatMoney(row?.chargeData?.amount || 0)} />
        )
      },
      {
        header: {
          title: '',
          disabled: true,
          textColor: theme.palette.primary.light,
          width: '50px',
          minWidth: '50px'
        },
        renderer: ({ row }: RendererParams) => {
          if (!isPersistedEntry(row)) {
            return (
              <IconButton
                size="small"
                aria-label={t('charges:add-entry')}
                onClick={() => handleEditEntry(row)}
              >
                <FontAwesomeIcon icon="plus" size="sm" />
              </IconButton>
            );
          }

          return (
            <InfoComponent
              onClickEdit={() => {
                handleEditEntry(row);
              }}
              onChangePayees={isPersistedEntry(row) && handleChangePayees ? () => {
                handleChangePayees(row);
              } : undefined}
              onChangePayers={isPersistedEntry(row) && handleChangePayers ? () => {
                handleChangePayers(row);
              } : undefined}
              onClickDelete={(row.chargeData.kind.includes('manual') && row.chargeData._id.length > 10) ? () => {
                handleDelete(row);
              } : undefined}
            />
          );
        }
      }
    ]
  },

});

export const generatePartiesCollapseRow = (
  t,
  onEditPayees?: (item: columnsDataSet) => void,
  onEditPayers?: (item: columnsDataSet) => void
): Column[] => [{
  renderer: ({ row, index, shouldCollapse }: RendererParams) => {
    const involvedParties = row?.chargeData?.involved_parties ?? [];
    const payees = involvedParties.filter((party) => party.kind.includes('Payee'));
    const payers = involvedParties.filter((party) => party.kind.includes('Payer'));

    return (
      <TableCell key={`collapse-column-${index}`} colSpan={5}>
        <Collapse in={shouldCollapse} timeout="auto" unmountOnExit>
          <Box style={{ display: 'flex', gap: '10px', padding: '10px 5px', flexWrap: 'wrap' }}>
            {payees.length > 0 && (
              <InvolvedPartyTable
                parties={payees}
                title={t('orders:payeesLabel')}
                onEditClick={() => onEditPayees?.(row)}
              />
            )}
            {payers.length > 0 && (
              <InvolvedPartyTable
                parties={payers}
                title={t('orders:payersLabel')}
                onEditClick={() => onEditPayers?.(row)}
              />
            )}
          </Box>
        </Collapse>
      </TableCell>
    );
  }
}];
